import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["list", "selectedId"]
  static values = { indexUrl: String }

  connect() {
    this.boundKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundKeydown)
    this.initializeList()
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundKeydown)
  }

  // Keyboard shortcuts

  handleKeydown(event) {
    if (!this.shortcutsAllowed(event)) return

    switch (event.key) {
      case "Escape":
        event.preventDefault()
        this.unselectAll()
        break
      case "e":
        event.preventDefault()
        this.archiveSelected()
        break
      case "u":
        event.preventDefault()
        this.toggleReadSelected()
        break
      case "j":
      case "ArrowDown":
        event.preventDefault()
        this.navigateDown()
        break
      case "k":
      case "ArrowUp":
        event.preventDefault()
        this.navigateUp()
        break
    }
  }

  unselectAll() {
    if (this.hasIndexUrlValue) {
      Turbo.visit(this.indexUrlValue)
    }
  }

  archiveSelected() {
    if (!this.hasSelectedIdTarget) return
    const url = this.selectedIdTarget.dataset.archiveUrl
    if (url) this.sendRequest(url, "PATCH")
  }

  toggleReadSelected() {
    if (!this.hasSelectedIdTarget) return
    const url = this.selectedIdTarget.dataset.toggleReadUrl
    if (url) this.sendRequest(url, "PATCH")
  }

  navigateDown() {
    const items = this.getNavigableItems()
    if (items.length === 0) return

    const current = this.getCurrentSelectedElement()
    const currentIndex = current ? items.indexOf(current) : -1
    const nextIndex = Math.min(currentIndex + 1, items.length - 1)
    this.clickItem(items[nextIndex])
  }

  navigateUp() {
    const items = this.getNavigableItems()
    if (items.length === 0) return

    const current = this.getCurrentSelectedElement()
    const currentIndex = current ? items.indexOf(current) : items.length
    const prevIndex = Math.max(currentIndex - 1, 0)
    this.clickItem(items[prevIndex])
  }

  getNavigableItems() {
    if (!this.hasListTarget) return []
    return Array.from(this.listTarget.querySelectorAll(":scope > [data-notification-id]:not(.hidden), :scope > [id^='notification_group']:not(.hidden)"))
  }

  getCurrentSelectedElement() {
    if (!this.hasSelectedIdTarget) return null
    const selectedItemId = this.selectedIdTarget.dataset.selectedItemId
    if (!selectedItemId) return null
    return document.getElementById(selectedItemId)
  }

  clickItem(item) {
    if (!item) return
    const link = item.querySelector("a[data-turbo-stream]")
    if (link) link.click()
  }

  sendPatch(event) {
    event.preventDefault()
    const url = event.currentTarget.dataset.url
    if (url) this.sendRequest(url, "PATCH")
  }

  sendRequest(url, method) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    fetch(url, {
      method,
      headers: {
        "Accept": "text/vnd.turbo-stream.html",
        "X-CSRF-Token": csrfToken
      }
    }).then(response => {
      if (response.ok) return response.text()
    }).then(html => {
      if (html) Turbo.renderStreamMessage(html)
    }).catch(() => {
      // silent fail for now — consider toast in a follow-up
    })
  }

  // Sort actions

  setSortNewest() {
    localStorage.setItem("inbox_sort", "newest")
    this.initializeList()
  }

  setSortOldest() {
    localStorage.setItem("inbox_sort", "oldest")
    this.initializeList()
  }

  initializeList() {
    const sort = localStorage.getItem("inbox_sort") || "newest"

    if (!this.hasListTarget) return

    // Sort: reverse children if oldest
    const children = Array.from(this.listTarget.children)
    if (children.length > 1) {
      const isReversed = this.listTarget.dataset.reversed === "true"
      if (sort === "oldest" && !isReversed) {
        children.reverse().forEach(child => this.listTarget.appendChild(child))
        this.listTarget.dataset.reversed = "true"
      } else if (sort === "newest" && isReversed) {
        children.reverse().forEach(child => this.listTarget.appendChild(child))
        this.listTarget.dataset.reversed = "false"
      }
    }

    // Update sort menu icons
    this.updateMenuState(sort)
  }

  updateMenuState(sort) {
    const newestIcon = this.element.querySelector("[data-filter-icon='sort-newest']")
    const oldestIcon = this.element.querySelector("[data-filter-icon='sort-oldest']")

    if (newestIcon) newestIcon.dataset.checked = sort === "newest"
    if (oldestIcon) oldestIcon.dataset.checked = sort === "oldest"
  }

  // URL

  replaceUrl(event) {
    const href = event.currentTarget.href
    if (href) history.replaceState({}, "", href)
  }

  // Helpers

  // Inbox shortcuts should only fire when the keystroke comes from the inbox
  // itself — either nothing is focused (body, i.e. navigating the list) or focus
  // is inside the inbox column. This excludes the detail/preview pane and the
  // new-post modal, which live outside this controller's element.
  shortcutsAllowed(event) {
    if (this.isTyping(event)) return false
    const target = event.target
    return target === document.body || this.element.contains(target)
  }

  isTyping(event) {
    const target = event.target
    if (!(target instanceof Element)) return false
    return target.closest(
      'input, textarea, select, [contenteditable=""], [contenteditable="true"], [role="textbox"]'
    ) !== null
  }
}
